import axios from "axios";

export type ReleaseChannel = "latest" | "beta" | "alpha";

export type PatchNoteRelease = {
  id: string;
  date: string;
  channel: ReleaseChannel;
  launcherVersion: string | null;
  gamesVersion: string | null;
  title: string;
  file: string;
};

export type PatchNotesManifest = {
  releases: PatchNoteRelease[];
};

export const PATCH_NOTES_CDN_URL = "https://herozone2.b-cdn.net/patch-notes/";
/** Same-origin proxy to avoid CORS (dev: setupProxy.js, prod: netlify.toml) */
export const PATCH_NOTES_BASE_URL = "/patch-notes-proxy/";

const CHANNEL_RANK: Record<ReleaseChannel, number> = {
  latest: 0,
  beta: 1,
  alpha: 2,
};

export function normalizePatchNoteChannel(channel: string): ReleaseChannel {
  if (channel === "stable") return "latest";
  return channel as ReleaseChannel;
}

export function resolvePatchNoteImageUrl(src: string | undefined): string {
  if (!src) return "";
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  let normalized = src.replace(/^\.\.\//, "").replace(/^\.\//, "");
  if (!normalized.includes("/")) {
    normalized = `images/${normalized}`;
  }
  return PATCH_NOTES_CDN_URL + normalized;
}

export function getVisibleReleases(
  releases: PatchNoteRelease[],
  userChannel: ReleaseChannel
): PatchNoteRelease[] {
  return [...releases]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .filter(
      (release) =>
        CHANNEL_RANK[normalizePatchNoteChannel(release.channel)] <=
        CHANNEL_RANK[userChannel]
    );
}

export async function fetchPatchNotesManifest(): Promise<PatchNotesManifest> {
  const { data } = await axios.get<PatchNotesManifest>(
    `${PATCH_NOTES_BASE_URL}manifest.json?timestamp=${Date.now()}`
  );
  return data;
}

const releaseContentCache = new Map<string, string>();

export async function fetchPatchNoteRelease(file: string): Promise<string> {
  const cached = releaseContentCache.get(file);
  if (cached) return cached;

  const { data } = await axios.get<string>(
    `${PATCH_NOTES_BASE_URL}${file}?timestamp=${Date.now()}`,
    { responseType: "text" }
  );
  releaseContentCache.set(file, data);
  return data;
}

export function getPatchNoteReleasePath(releaseId: string): string {
  return `/patch-notes/${encodeURIComponent(releaseId)}`;
}

export function getPatchNoteReleaseUrl(releaseId: string): string {
  return `${window.location.origin}${getPatchNoteReleasePath(releaseId)}`;
}

export function findPatchNoteRelease(
  releases: PatchNoteRelease[],
  releaseId: string
): PatchNoteRelease | undefined {
  const decodedId = decodeURIComponent(releaseId);
  return releases.find((release) => release.id === decodedId);
}
